import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { logoutThunk } from "reduxStore/auth/thunks";
import { AdminRoutes, CoachRoutes, StudentRoutes } from "config/routes";
import { useAppDispatch } from "hooks/useAppDispatch";
import { Link, useNavigate } from "react-router-dom";
import { StyledAppBar } from "./styles";

import {
  Avatar,
  Backdrop,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import MenuIcon from "@mui/icons-material/Menu";
import { useAppSelector } from "hooks/useAppSelector";
import { authStatusSelector, userSelector } from "reduxStore/auth/selectors";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import {
  Search,
  Person,
  Feedback,
  BugReport,
  RocketLaunch,
  Pets,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { getLettersForUserName } from "utils/user";
import {
  useGetUserByIdLazyQuery,
  useUpdateUserMutation,
} from "graphql/__generated__/hooks";
import { useQuery } from "@apollo/client";
import { GET_USER_BADGES } from "graphql/badge/badge.query";
import { ROLES, User } from "types/user";
import { BadgeDisplay } from "components/Badge/BadgeDisplay";
import { BadgeAnimationModal } from "components/Badge/BadgeAnimationModal";
import { Badge } from "types/badge";
import {
  CompleteOnboardingButton,
  ResponsiveButtonLink,
} from "components/Button";
import ConfirmActionModal from "../../modals/ConfirmActionModal/ConfirmActionModal";
import { Messages, NotificationSeverity } from "../../helpers/constants";
import { useNotifications } from "../../hooks/useNotifications";
import { BugReportAndSuggestionsModal } from "../../modals/BugReportAndSuggestionsModal";
import { HeaderNotificationMenu } from "./HeaderNotificationMenu";
import { useNotificationUnreadCount } from "hooks/useNotificationUnreadCount";
import { useVetcoin } from "hooks/useVetcoin";
import { VETCOIN_BALANCE_TARGET_ID } from "utils/vetcoinAnimation";
import { VetcoinFlyAnimation } from "components/Vetcoin/VetcoinFlyAnimation";
import { StoreItemPreview } from "components/VetStore/StoreItemPreview";
import {
  APP_HEADER_HEIGHT_CSS_VAR,
  getAppHeaderHeightPx,
} from "helpers/headerLayout";
import {
  getLinkedAccounts,
  storeAppSession,
  switchLinkedAccount,
} from "services/accountSession";
import { LinkedAccountProfile } from "types/auth";
import { SwitchAccountDialog } from "./SwitchAccountDialog";
import client from "utils/ApolloClient";
import { SelectOrganizationThunk } from "reduxStore/organization/thunks";

type Props = {
  handleDrawerMenuOpen: () => void;
  isDrawerMenuOpen: boolean;
};

const Header: React.FC<Props> = ({
  handleDrawerMenuOpen,
  isDrawerMenuOpen,
}) => {
  const dispatch = useAppDispatch();
  const statuses = useAppSelector(authStatusSelector);
  const navigate = useNavigate();
  const user = useAppSelector(userSelector);
  const loading = statuses["auth/logoutThunk"].pending;
  const [getUserData, { data: userData }] = useGetUserByIdLazyQuery();
  const { data: userBadgesData } = useQuery(GET_USER_BADGES, {
    variables: { userId: user?.user_id || "" },
    skip: !user?.user_id,
  });
  const [
    showBugReportAndSuggestionsModal,
    setShowBugReportAndSuggestionsModal,
  ] = useState(false);
  const [selectedBadges, setSelectedBadges] = useState<Badge[]>([]);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const { notify } = useNotifications();
  const { unreadCount, refetchUnreadCount } = useNotificationUnreadCount();
  const { balance, getEquippedByType } = useVetcoin(user?.user_id);
  const theme = useTheme();
  const isBelowDesktop = useMediaQuery(theme.breakpoints.down("md"));
  const [showMobileCompanionBar, setShowMobileCompanionBar] = useState(true);

  const avatarPicture = useMemo(
    () => userData?.user_by_pk?.picture_url,
    [userData],
  );
  const graduationClass = useMemo(() => {
    return userData?.graduation_class_student[0]?.graduation_class || null;
  }, [userData]);

  const userOrganizationId = useMemo(() => {
    return userData?.user_by_pk?.org_id;
  }, [userData]);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccountProfile[]>(
    [],
  );
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showSwitchAccountDialog, setShowSwitchAccountDialog] = useState(false);
  const [switchingUserId, setSwitchingUserId] = useState<string | null>(null);

  const handleLogout = async () => {
    dispatch(logoutThunk());
    navigate(AdminRoutes.Login);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
    setLoadingAccounts(true);
    getLinkedAccounts()
      .then(({ accounts }) => setLinkedAccounts(accounts))
      .catch(() => setLinkedAccounts([]))
      .finally(() => setLoadingAccounts(false));
  };

  const handleSwitchAccount = async (account: LinkedAccountProfile) => {
    setSwitchingUserId(account.user_id);
    try {
      const replacement = await switchLinkedAccount(account.user_id);
      await client.clearStore();
      await dispatch(SelectOrganizationThunk(null));
      storeAppSession(replacement);
      window.location.replace(AdminRoutes.Home);
    } catch {
      notify({
        message:
          "Account could not be switched. Your current session is unchanged.",
        severity: NotificationSeverity.error,
      });
      setSwitchingUserId(null);
    }
  };

  const handleOpenBugReportAndSuggestionsModal = () => {
    setShowBugReportAndSuggestionsModal(true);
  };

  const onSubmitBugReportAndSuggestions = (report: string) => {
    notify({
      message: Messages.successSubmittedBugReportOrSuggestion,
      severity: NotificationSeverity.success,
    });
    setShowBugReportAndSuggestionsModal(false);
  };

  const handleSettingsMenuClose = () => {
    setAnchorElUser(null);
  };

  useEffect(() => {
    if (user?.user_id) {
      getUserData({
        variables: {
          userId: user.user_id,
        },
      });
    }
  }, [user]);

  useEffect(() => {
    const hiddenState =
      window.localStorage.getItem("vetnebula:hide-mobile-companion-bar") ===
      "true";
    setShowMobileCompanionBar(!hiddenState);
  }, []);

  const equippedPet = getEquippedByType("PET")?.store_item;
  const mobileCompanionVisible =
    showMobileCompanionBar && !!equippedPet && isBelowDesktop;

  useLayoutEffect(() => {
    const height = getAppHeaderHeightPx({
      showMobileCompanionBar: mobileCompanionVisible,
      isBelowDesktop,
    });
    document.documentElement.style.setProperty(
      APP_HEADER_HEIGHT_CSS_VAR,
      `${height}px`,
    );
  }, [mobileCompanionVisible, isBelowDesktop]);

  const toggleMobileCompanionBar = () => {
    setShowMobileCompanionBar((previousValue) => {
      const nextValue = !previousValue;
      window.localStorage.setItem(
        "vetnebula:hide-mobile-companion-bar",
        String(!nextValue),
      );
      return nextValue;
    });
  };

  return (
    <>
      <StyledAppBar
        position="fixed"
        variant={"elevation"}
        color={"default"}
        open={isDrawerMenuOpen}
        showMobileCompanionBar={mobileCompanionVisible}
      >
        <Container maxWidth={false}>
          <Toolbar
            disableGutters
            sx={{
              justifyContent: { xs: "space-between", md: "initial" },
            }}
          >
            <IconButton
              size="large"
              edge="start"
              aria-label="open drawer"
              sx={{
                mr: 2,
                display: { xs: "flex", md: "none" },
                color: "white",
              }}
              onClick={handleDrawerMenuOpen}
            >
              <MenuIcon />
            </IconButton>

            {equippedPet && (
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: 1,
                  zIndex: 1,
                }}
              >
                <Tooltip title={`VetPet: ${equippedPet.name}`}>
                  <Box sx={{ width: 70 }}>
                    <StoreItemPreview item={equippedPet} size="small" chromeless />
                  </Box>
                </Tooltip>
              </Box>
            )}

            <Stack
              className="header-text"
              spacing={0}
              flexGrow={1}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h5"
                  noWrap
                  sx={{ display: { xs: "none", md: "flex" } }}
                >
                  Welcome, {user?.name}!
                </Typography>
                {userBadgesData?.user_badge &&
                  userBadgesData.user_badge.length > 0 && (
                    <BadgeDisplay
                      badges={userBadgesData.user_badge as any}
                      size="small"
                      onBadgeClick={(badge) => {
                        setSelectedBadges([badge]);
                        setShowBadgeModal(true);
                      }}
                    />
                  )}
              </Box>
              <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
                <Typography
                  id={!isBelowDesktop ? VETCOIN_BALANCE_TARGET_ID : undefined}
                  variant="body2"
                  sx={{
                    mr: 1,
                    "@keyframes vetcoinBalancePulse": {
                      "0%": { transform: "scale(1)" },
                      "40%": { transform: "scale(1.18)", color: "#ffe566" },
                      "100%": { transform: "scale(1)" },
                    },
                    "&.vetcoin-balance-pulse": {
                      animation: "vetcoinBalancePulse 450ms ease-out",
                    },
                  }}
                  noWrap
                >
                  <b>Vetcoins:</b> {balance}
                </Typography>
                <Typography variant="body2" sx={{ mr: 1 }} noWrap>
                  <b>Email:</b> {user?.email}
                </Typography>
                {user?.role !== ROLES.STUDENT && user?.role !== ROLES.COACH && (
                  <Typography variant="body2" sx={{ mr: 1 }} noWrap>
                    <b>Role:</b> {user?.role?.toUpperCase()}
                  </Typography>
                )}
                {user?.role === ROLES.STUDENT && graduationClass && (
                  <Typography variant="body2" sx={{}} noWrap>
                    - {graduationClass.name}
                  </Typography>
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography
                id={
                  isBelowDesktop ? VETCOIN_BALANCE_TARGET_ID : undefined
                }
                variant="body2"
                noWrap
                sx={{
                  display: { xs: "flex", md: "none" },
                  color: "white",
                  fontWeight: 600,
                  "@keyframes vetcoinBalancePulse": {
                    "0%": { transform: "scale(1)" },
                    "40%": { transform: "scale(1.18)", color: "#ffe566" },
                    "100%": { transform: "scale(1)" },
                  },
                  "&.vetcoin-balance-pulse": {
                    animation: "vetcoinBalancePulse 450ms ease-out",
                  },
                }}
              >
                {balance} VC
              </Typography>
              <CompleteOnboardingButton />
              {user?.role === "student" &&
                !graduationClass &&
                userOrganizationId && (
                  <Box mr={1}>
                    <ResponsiveButtonLink
                      variant="outlined"
                      to={`/organization/${userOrganizationId}/onboarding-students`}
                      icon={<RocketLaunch />}
                      sx={{
                        color: "white",
                        border: "1px solid white",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1) !important",
                          borderColor: "rgba(255,255,255,0.9) !important",
                        },
                      }}
                    >
                      Setup Account
                    </ResponsiveButtonLink>
                  </Box>
                )}
              {user?.role === "student" && graduationClass && (
                <Box mr={1}>
                  <ResponsiveButtonLink
                    variant="outlined"
                    to={StudentRoutes.RequestFeedback}
                    icon={<Feedback />}
                    sx={{
                      color: "white",
                      border: "1px solid white",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1) !important",
                        borderColor: "rgba(255,255,255,0.9) !important",
                      },
                    }}
                  >
                    Request Feedback
                  </ResponsiveButtonLink>
                </Box>
              )}
              {user?.role === "coach" && (
                <Box mr={1}>
                  <ResponsiveButtonLink
                    variant="outlined"
                    to={CoachRoutes.GiveFeedback}
                    icon={<Feedback />}
                    sx={{
                      color: "white",
                      border: "1px solid white",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1) !important",
                        borderColor: "rgba(255,255,255,0.9) !important",
                      },
                    }}
                  >
                    Give Feedback
                  </ResponsiveButtonLink>
                </Box>
              )}

              <Box mr={1} display="none">
                <IconButton>
                  <Search
                    sx={{
                      color: "white",
                    }}
                  />
                </IconButton>
              </Box>

              <Box sx={{ mr: 0.5 }}>
                <HeaderNotificationMenu
                  unreadCount={unreadCount}
                  refetchUnreadCount={refetchUnreadCount}
                />
              </Box>

              {equippedPet && (
                <Tooltip
                  title={
                    showMobileCompanionBar
                      ? "Hide companion bar"
                      : "Show companion bar"
                  }
                >
                  <IconButton
                    sx={{
                      color: "white",
                      display: { xs: "inline-flex", md: "none" },
                    }}
                    onClick={toggleMobileCompanionBar}
                    size={"large"}
                  >
                    {showMobileCompanionBar ? <KeyboardArrowUp /> : <Pets />}
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Bug Report & Suggestions">
                <IconButton
                  sx={{
                    color: "white",
                  }}
                  onClick={handleOpenBugReportAndSuggestionsModal}
                  size={"large"}
                >
                  <BugReport />
                </IconButton>
              </Tooltip>

              <Box sx={{ flexGrow: 0 }}>
                <Tooltip title="Open settings">
                  {avatarPicture ? (
                    <Avatar
                      aria-label="Open settings"
                      onClick={handleOpenUserMenu}
                      sx={{ cursor: "pointer" }}
                      src={avatarPicture}
                      alt={user?.name}
                    />
                  ) : (
                    <IconButton
                      aria-label="Open settings"
                      onClick={handleOpenUserMenu}
                      size={"large"}
                      sx={{
                        backgroundColor: "white",
                        width: 40,
                        height: 40,
                        fontSize: "1rem",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.8) !important",
                          borderColor: "rgba(255,255,255,0.9) !important",
                        },
                      }}
                    >
                      {user?.name ? (
                        getLettersForUserName(user?.name)
                      ) : (
                        <Person />
                      )}
                    </IconButton>
                  )}
                </Tooltip>
                <Menu
                  sx={{ mt: "45px", zIndex: "1000000000001" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleSettingsMenuClose}
                >
                  <MenuItem
                    disableRipple
                    disableTouchRipple
                    sx={{ typography: "body2", minWidth: "200px" }}
                    component={Link}
                    to={AdminRoutes.Profile}
                    onClick={handleSettingsMenuClose}
                  >
                    <Typography textAlign="center">Profile</Typography>
                  </MenuItem>

                  <MenuItem
                    disableRipple
                    disableTouchRipple
                    disabled={loadingAccounts || linkedAccounts.length <= 1}
                    sx={{ typography: "body2", minWidth: "200px" }}
                    onClick={() => {
                      setAnchorElUser(null);
                      setShowSwitchAccountDialog(true);
                    }}
                  >
                    <Typography textAlign="center">
                      {loadingAccounts
                        ? "Loading accounts..."
                        : "Switch account"}
                    </Typography>
                  </MenuItem>

                  <Divider sx={{ borderStyle: "dashed", m: 0 }} />

                  <MenuItem
                    disableRipple
                    disableTouchRipple
                    onClick={handleLogout}
                    sx={{
                      typography: "body2",
                      color: "error.main",
                      py: 1.5,
                      minWidth: "200px",
                    }}
                  >
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            </Stack>
          </Toolbar>
        </Container>

        {showMobileCompanionBar && equippedPet && (
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pb: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 72 }}>
                <StoreItemPreview item={equippedPet} size="small" chromeless />
              </Box>
              <Typography variant="body2" noWrap>
                {equippedPet.name}
              </Typography>
            </Stack>
          </Box>
        )}

        {showBugReportAndSuggestionsModal && (
          <BugReportAndSuggestionsModal
            open={showBugReportAndSuggestionsModal}
            onClose={() => setShowBugReportAndSuggestionsModal(false)}
            onSubmit={(report) => {
              onSubmitBugReportAndSuggestions(report);
            }}
          />
        )}

        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <Stack direction="column" alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="subtitle2" noWrap>
              Signing Out...
            </Typography>
          </Stack>
        </Backdrop>

        <BadgeAnimationModal
          badges={selectedBadges}
          open={showBadgeModal}
          onClose={() => {
            setShowBadgeModal(false);
            setSelectedBadges([]);
          }}
        />
        <SwitchAccountDialog
          open={showSwitchAccountDialog}
          accounts={linkedAccounts}
          currentUserId={user?.user_id}
          loading={loadingAccounts}
          switchingUserId={switchingUserId}
          onClose={() => setShowSwitchAccountDialog(false)}
          onSelect={handleSwitchAccount}
        />
      </StyledAppBar>
      <VetcoinFlyAnimation />
    </>
  );
};

export default Header;
